import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Difficulty } from "@hunch/core";
import { GAME_BRANDING } from "@hunch/branding";
import { GameIcon } from "../../components/illustrations/GameIcon.js";
import { DifficultyInfoBox } from "../../components/game/DifficultyInfoBox.js";
import { useI18n } from "../../i18n/index.js";
import { readLastOptions, writeLastOptions } from "../../lib/lastOptions.js";
import { ROUND_TIME_SECONDS } from "../../lib/roundTime.js";
import {
  fetchTimeslipCategories,
  fetchTimeslipPoolSize,
  type TimeslipCategory,
  type TimeslipPoolSize,
} from "./api.js";
import styles from "./TimeslipStart.module.css";

export interface TimeslipOptions {
  categories: "all" | string[];
  difficulty: Difficulty;
}

const DIFFICULTIES: Difficulty[] = ["easy", "normal", "hard"];

export function TimeslipStart({ onStart }: { onStart: (options: TimeslipOptions) => void }) {
  const { lang, t } = useI18n();
  const [categories, setCategories] = useState<TimeslipCategory[] | "loading" | "error">("loading");

  const saved = readLastOptions<TimeslipOptions>("timeslip");
  const [selected, setSelected] = useState<"all" | string[]>(saved?.categories ?? "all");
  const [difficulty, setDifficulty] = useState<Difficulty>(saved?.difficulty ?? "normal");
  const [poolSize, setPoolSize] = useState<TimeslipPoolSize | "loading" | "error">("loading");

  useEffect(() => {
    fetchTimeslipCategories()
      .then(setCategories)
      .catch(() => setCategories("error"));
  }, []);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    setPoolSize("loading");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchTimeslipPoolSize(selected, difficulty)
        .then(setPoolSize)
        .catch(() => setPoolSize("error"));
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [selected, difficulty]);

  function toggleCategory(slug: string) {
    setSelected((prev) => {
      if (prev === "all") return [slug];
      const next = prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug];
      return next.length === 0 ? "all" : next;
    });
  }

  const canStart = poolSize !== "loading" && poolSize !== "error" && poolSize.enough;

  function handleStart() {
    if (!canStart) return;
    const options: TimeslipOptions = { categories: selected, difficulty };
    writeLastOptions("timeslip", options);
    onStart(options);
  }

  return (
    <main className={styles.main}>
      <GameIcon gameId="timeslip" className={styles.icon} />
      <h1 className={styles.title}>{GAME_BRANDING.timeslip.name}</h1>
      <p className={styles.tagline}>{t("timeslip.tagline")}</p>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t("timeslip.start.categoriesLabel")}</h2>
        {categories === "loading" && <p className={styles.status}>{t("common.loading")}</p>}
        {categories === "error" && <p className={styles.statusError}>{t("timeslip.start.loadError")}</p>}
        {Array.isArray(categories) && (
          <div className={styles.chips} role="group" aria-label={t("timeslip.start.categoriesLabel")}>
            <button
              type="button"
              className={styles.chip}
              aria-pressed={selected === "all"}
              onClick={() => setSelected("all")}
            >
              {t("timeslip.start.allCategories")}
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                type="button"
                className={styles.chip}
                aria-pressed={selected !== "all" && selected.includes(c.slug)}
                onClick={() => toggleCategory(c.slug)}
              >
                {c.name[lang]}
              </button>
            ))}
          </div>
        )}
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t("timeslip.start.difficultyLabel")}</h2>
        <div className={styles.segmented} role="group" aria-label={t("timeslip.start.difficultyLabel")}>
          {DIFFICULTIES.map((d) => (
            <button
              key={d}
              type="button"
              className={styles.segment}
              aria-pressed={difficulty === d}
              onClick={() => setDifficulty(d)}
            >
              {t(`timeslip.start.difficulty.${d}`)}
            </button>
          ))}
        </div>
        <DifficultyInfoBox
          description={t(`timeslip.start.difficultyInfo.${difficulty}`)}
          roundLimitSeconds={ROUND_TIME_SECONDS[difficulty]}
          noLimitLabel={t("common.noTimeLimit")}
        />
      </section>

      {poolSize !== "loading" && poolSize !== "error" && !poolSize.enough && (
        <p className={styles.hint}>{t("timeslip.start.notEnoughCards")}</p>
      )}

      <button type="button" className={styles.startButton} disabled={!canStart} onClick={handleStart}>
        {t("timeslip.start.startButton")}
      </button>
      <Link to="/" className={styles.backLink}>
        ← {t("common.backHome")}
      </Link>
    </main>
  );
}
